import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  updateCompanySchema,
  type UpdateAISettingsInput,
  inviteSchema,
  type UpdateCompanyInput,
  type InviteInput,
  type Company,
  type CompanyMember,
  type CrmType,
  type Invite,
  type AIConfig,
} from '@octery/shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type InviteWithToken = Invite & { token?: string };
type InviteCreateResponse = Invite & {
  inviteUrl: string;
  token?: string;
};

// ============ Automation Options Section ============
function AutomationOptionsCard() {
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => api.get<AIConfig>('/api/ai-settings'),
  });

  const mutation = useMutation({
    mutationFn: (data: UpdateAISettingsInput) => api.patch<AIConfig>('/api/ai-settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
      toast.success('Automation settings updated');
    },
    onError: () => {
      toast.error('Failed to update automation settings');
    },
  });

  const handleToggle = (key: 'needCalendar' | 'needConsultationOption', value: boolean) => {
    mutation.mutate({ [key]: value });
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-5 bg-muted rounded w-1/3" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-10 bg-muted rounded" />
          <div className="h-10 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Conversation Options</CardTitle>
        <CardDescription>
          Control wizard/automation behavior exposed to internal config by slug
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-start justify-between gap-4 border rounded-md p-3">
          <div>
            <p className="text-sm font-medium">Need calendar</p>
            <p className="text-xs text-muted-foreground">
              Enables calendar requirement in the intake flow
            </p>
          </div>
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={Boolean(config?.needCalendar)}
            disabled={mutation.isPending}
            onChange={(e) => handleToggle('needCalendar', e.target.checked)}
          />
        </label>

        <label className="flex items-start justify-between gap-4 border rounded-md p-3">
          <div>
            <p className="text-sm font-medium">Need consultation option</p>
            <p className="text-xs text-muted-foreground">
              Enables consultation option in the intake flow
            </p>
          </div>
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={Boolean(config?.needConsultationOption)}
            disabled={mutation.isPending}
            onChange={(e) => handleToggle('needConsultationOption', e.target.checked)}
          />
        </label>
      </CardContent>
    </Card>
  );
}

// ============ Company Info Section ============
function CompanyInfoCard() {
  const queryClient = useQueryClient();

  const { data: company, isLoading } = useQuery({
    queryKey: ['company'],
    queryFn: () => api.get<Company>('/api/companies/current'),
  });

  const mutation = useMutation({
    mutationFn: (data: UpdateCompanyInput) =>
      api.patch<Company>('/api/companies/current', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      toast.success('Company updated successfully');
    },
    onError: () => {
      toast.error('Failed to update company');
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UpdateCompanyInput>({
    resolver: zodResolver(updateCompanySchema),
    values: company
      ? {
          name: company.name,
          timezone: company.timezone,
          crmType: company.crmType,
          crmSubdomain: company.crmSubdomain ?? null,
          bitrixWebhookUrl: company.bitrixWebhookUrl ?? null,
        }
      : undefined,
  });

  const selectedCrmType = (watch('crmType') ?? company?.crmType ?? 'amocrm') as CrmType;
  const watchedWebhook = watch('bitrixWebhookUrl');
  const [showWebhook, setShowWebhook] = useState(false);

  // Derived portal host preview for the Bitrix card.
  let derivedHost: string | null = null;
  if (selectedCrmType === 'bitrix24' && watchedWebhook) {
    try {
      derivedHost = new URL(watchedWebhook).hostname;
    } catch {
      derivedHost = null;
    }
  }

  // When the user flips CRM type, blank out the other CRM's field so the
  // PATCH payload doesn't carry stale state from the previous selection.
  useEffect(() => {
    if (selectedCrmType === 'amocrm') {
      setValue('bitrixWebhookUrl', null, { shouldValidate: false });
    } else {
      setValue('crmSubdomain', null, { shouldValidate: false });
    }
  }, [selectedCrmType, setValue]);

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-5 bg-muted rounded w-1/3" />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="h-10 bg-muted rounded" />
          <div className="h-10 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Company Information</CardTitle>
        <CardDescription>Manage your company name, timezone, and branding</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Company Name</label>
              <Input {...register('name')} placeholder="Acme Inc." />
              {errors.name && (
                <p className="text-destructive text-xs">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Timezone</label>
              <Input {...register('timezone')} placeholder="America/New_York" />
              {errors.timezone && (
                <p className="text-destructive text-xs">{errors.timezone.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">CRM Type</label>
            <select
              {...register('crmType')}
              className="h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="amocrm">amoCRM</option>
              <option value="bitrix24">Bitrix24</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Determines how Octery routes incoming messages and which credentials are used for outbound calls.
            </p>
          </div>

          {selectedCrmType === 'amocrm' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">amoCRM Subdomain</label>
              <div className="flex items-center gap-2">
                <Input
                  {...register('crmSubdomain')}
                  placeholder="mycompany"
                  className="max-w-xs"
                />
                <span className="text-sm text-muted-foreground">.amocrm.ru</span>
              </div>
              {errors.crmSubdomain && (
                <p className="text-destructive text-xs">{errors.crmSubdomain.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                The subdomain of your amoCRM account. Used to route incoming messages to this company.
              </p>
            </div>
          )}

          {selectedCrmType === 'bitrix24' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Bitrix24 Incoming Webhook URL</label>
              <div className="flex items-center gap-2">
                <Input
                  {...register('bitrixWebhookUrl')}
                  type={showWebhook ? 'text' : 'password'}
                  placeholder="https://b24-xxxxx.bitrix24.ru/rest/1/<secret>/"
                  className="flex-1 max-w-xl font-mono text-xs"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowWebhook((v) => !v)}
                >
                  {showWebhook ? 'Hide' : 'Show'}
                </Button>
              </div>
              {errors.bitrixWebhookUrl && (
                <p className="text-destructive text-xs">{errors.bitrixWebhookUrl.message}</p>
              )}
              {derivedHost && (
                <p className="text-xs text-muted-foreground">
                  Portal: <code className="bg-muted px-1 rounded">{derivedHost}</code>
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Create in Bitrix24 → «Разработчикам» → «Входящий вебхук» with at minimum the <code>crm</code> permission.
                The URL embeds the auth secret — treat it like a password.
              </p>
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            Slug: <code className="bg-muted px-1 rounded">{company?.slug}</code> · Created:{' '}
            {company?.createdAt ? new Date(company.createdAt).toLocaleDateString() : '—'}
          </div>

          <div className="flex justify-end gap-3">
            <Button type="submit" disabled={!isDirty || mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>

          {mutation.isSuccess && (
            <p className="text-green-600 text-sm text-right">Company updated!</p>
          )}
          {mutation.isError && (
            <p className="text-destructive text-sm text-right">Failed to update company</p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// ============ Team Members Section ============
function TeamMembersCard() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isOwnerOrAdmin = currentUser?.role === 'owner' || currentUser?.role === 'admin';

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['company', 'members'],
    queryFn: () => api.get<CompanyMember[]>('/api/companies/current/members'),
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) =>
      api.delete(`/api/companies/current/members/${memberId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'members'] });
      toast.success('Member removed');
    },
    onError: () => {
      toast.error('Failed to remove member');
    },
  });

  const roleColors: Record<string, string> = {
    owner: 'bg-amber-100 text-amber-800',
    admin: 'bg-blue-100 text-blue-800',
    viewer: 'bg-gray-100 text-gray-800',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Team Members</CardTitle>
        <CardDescription>
          {members.length} member{members.length !== 1 ? 's' : ''} in your organization
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="divide-y">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-medium">
                    {member.user.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{member.user.name}</p>
                    <p className="text-xs text-muted-foreground">{member.user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleColors[member.role] || 'bg-gray-100'}`}
                  >
                    {member.role}
                  </span>
                  {isOwnerOrAdmin &&
                    member.role !== 'owner' &&
                    member.userId !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={removeMutation.isPending}
                        onClick={() => {
                          if (confirm(`Remove ${member.user.name} from the team?`)) {
                            removeMutation.mutate(member.id);
                          }
                        }}
                      >
                        Remove
                      </Button>
                    )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ Invites Section ============
function InvitesCard() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingInviteId, setEditingInviteId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<'viewer' | 'admin'>('viewer');
  const [editExpiresAt, setEditExpiresAt] = useState('');

  const { data: invites = [], isLoading } = useQuery({
    queryKey: ['company', 'invites'],
    queryFn: () => api.get<Invite[]>('/api/companies/current/invites'),
  });

  const createMutation = useMutation({
    mutationFn: (data: InviteInput) =>
      api.post<InviteCreateResponse>('/api/companies/current/invites', data),
    onSuccess: (createdInvite) => {
      queryClient.invalidateQueries({ queryKey: ['company', 'invites'] });
      setShowForm(false);
      reset();

      navigator.clipboard.writeText(createdInvite.inviteUrl).catch(() => {});
      toast.success('Invite link created and copied');
    },
    onError: () => {
      toast.error('Failed to create invite. The user may already be a member.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { inviteId: string; role: 'viewer' | 'admin'; expiresAt: string }) =>
      api.patch<Invite>(`/api/companies/current/invites/${payload.inviteId}`, {
        role: payload.role,
        expiresAt: new Date(payload.expiresAt).toISOString(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'invites'] });
      setEditingInviteId(null);
      toast.success('Invite updated');
    },
    onError: () => {
      toast.error('Failed to update invite');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (inviteId: string) => api.delete(`/api/companies/current/invites/${inviteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', 'invites'] });
      toast.success('Invite canceled');
    },
    onError: () => {
      toast.error('Failed to cancel invite');
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteInput>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { role: 'viewer' },
  });

  const handleCopyLink = (invite: InviteWithToken) => {
    const url = `${window.location.origin}/invite/${invite.token || invite.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(invite.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const startEdit = (invite: Invite) => {
    setEditingInviteId(invite.id);
    setEditRole(invite.role as 'viewer' | 'admin');
    setEditExpiresAt(new Date(invite.expiresAt).toISOString().slice(0, 10));
  };

  const activeInvites = invites.filter((invite) => new Date(invite.expiresAt) >= new Date());
  const expiredInvites = invites.filter((invite) => new Date(invite.expiresAt) < new Date());

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Pending Invites</CardTitle>
          <CardDescription>Invite new members to your team</CardDescription>
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Invite'}
        </Button>
      </CardHeader>
      <CardContent>
        {showForm && (
          <form
            onSubmit={handleSubmit((data) => createMutation.mutate(data))}
            className="mb-4 p-4 border rounded-lg space-y-3"
          >
            <div className="grid gap-3 md:grid-cols-3">
              <div className="md:col-span-2 space-y-1">
                <label className="text-sm font-medium">Email</label>
                <Input {...register('email')} placeholder="colleague@example.com" />
                {errors.email && (
                  <p className="text-destructive text-xs">{errors.email.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Role</label>
                <select
                  {...register('role')}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Invite Link'}
              </Button>
            </div>
            {createMutation.isError && (
              <p className="text-destructive text-xs">
                Failed to create invite. The user may already be a member.
              </p>
            )}
          </form>
        )}

        {isLoading ? (
          <div className="h-20 bg-muted rounded animate-pulse" />
        ) : invites.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No pending invites
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">Active ({activeInvites.length})</p>
              {activeInvites.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No active invites</p>
              ) : (
                <div className="divide-y border rounded-md px-3">
                  {activeInvites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between py-3 first:pt-3 last:pb-3"
                    >
                      {editingInviteId === invite.id ? (
                        <div className="w-full grid gap-2 md:grid-cols-[1fr_140px_150px_auto_auto] items-center">
                          <p className="text-sm font-medium truncate">{invite.email}</p>
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value as 'viewer' | 'admin')}
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="admin">Admin</option>
                          </select>
                          <Input
                            type="date"
                            value={editExpiresAt}
                            onChange={(e) => setEditExpiresAt(e.target.value)}
                          />
                          <Button
                            size="sm"
                            disabled={updateMutation.isPending || !editExpiresAt}
                            onClick={() => updateMutation.mutate({
                              inviteId: invite.id,
                              role: editRole,
                              expiresAt: editExpiresAt,
                            })}
                          >
                            Save
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingInviteId(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="text-sm font-medium">{invite.email}</p>
                            <p className="text-xs text-muted-foreground">
                              Role: {invite.role} · Expires: {new Date(invite.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyLink(invite)}
                            >
                              {copiedId === invite.id ? '✓ Copied' : 'Copy Link'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => startEdit(invite)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={cancelMutation.isPending}
                              onClick={() => cancelMutation.mutate(invite.id)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {expiredInvites.length > 0 && (
              <details className="border rounded-md p-3">
                <summary className="cursor-pointer text-sm font-medium">
                  Expired invites ({expiredInvites.length})
                </summary>
                <div className="mt-3 divide-y">
                  {expiredInvites.map((invite) => (
                    <div key={invite.id} className="flex items-center justify-between py-3 first:pt-0 last:pb-0">
                      {editingInviteId === invite.id ? (
                        <div className="w-full grid gap-2 md:grid-cols-[1fr_140px_150px_auto_auto] items-center">
                          <p className="text-sm font-medium truncate">{invite.email}</p>
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value as 'viewer' | 'admin')}
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="admin">Admin</option>
                          </select>
                          <Input
                            type="date"
                            value={editExpiresAt}
                            onChange={(e) => setEditExpiresAt(e.target.value)}
                          />
                          <Button
                            size="sm"
                            disabled={updateMutation.isPending || !editExpiresAt}
                            onClick={() => updateMutation.mutate({
                              inviteId: invite.id,
                              role: editRole,
                              expiresAt: editExpiresAt,
                            })}
                          >
                            Save
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingInviteId(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="text-sm font-medium">{invite.email}</p>
                            <p className="text-xs text-muted-foreground">
                              Role: {invite.role} · Expired: {new Date(invite.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => startEdit(invite)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={cancelMutation.isPending}
                              onClick={() => cancelMutation.mutate(invite.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ Main Settings Page ============
export function CompanySettingsPage() {
  const currentUser = useAuthStore((s) => s.user);
  const isOwnerOrAdmin = currentUser?.role === 'owner' || currentUser?.role === 'admin';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your company, team, and invitations</p>
      </div>

      <CompanyInfoCard />
      {isOwnerOrAdmin && <AutomationOptionsCard />}
      <TeamMembersCard />
      {isOwnerOrAdmin && <InvitesCard />}
    </div>
  );
}
